/**
 * AWS Cognito Authentication Service
 * 
 * Low-level wrapper around Amazon Cognito Identity SDK
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoRefreshToken
} from 'amazon-cognito-identity-js';

// Configuration from environment — these must be set via VITE_ env vars (see app/.env.example)
const USER_POOL_ID = import.meta.env.VITE_COGNITO_USER_POOL_ID;
const CLIENT_ID = import.meta.env.VITE_COGNITO_CLIENT_ID;

if (!USER_POOL_ID || !CLIENT_ID) {
  console.warn('[cognitoAuth] VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID not set (OK for tests)');
}

// Storage keys
const REFRESH_TOKEN_KEY = 'zecure_cognito_refresh_token';
const ACCESS_TOKEN_KEY = 'zecure_cognito_access_token';
const ID_TOKEN_KEY = 'zecure_cognito_id_token';

// Lazy-load User Pool (only create when needed, not at module import time)
// This allows tests to load this module without requiring Cognito credentials
let userPool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!userPool) {
    if (!USER_POOL_ID || !CLIENT_ID) {
      console.error(
        '[cognitoAuth] Missing VITE_COGNITO_USER_POOL_ID / VITE_COGNITO_CLIENT_ID — set them in app/.env',
      );
      throw new Error(
        'Sign-in is not available right now. Please contact your ZeeFleet administrator.',
      );
    }
    userPool = new CognitoUserPool({
      UserPoolId: USER_POOL_ID,
      ClientId: CLIENT_ID
    });
  }
  return userPool;
}

export interface CognitoTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
}

/**
 * Fixed local-dev user, used when Cognito is not configured.
 * Mirrors the server-side dev user (server/middleware/cognitoAuth.js and
 * zeecore's LOCAL_DEV_ACTOR_ID) so the UI and API agree on who is acting.
 * Returns null when Cognito is configured, or in a production build —
 * a missing env var must never silently auto-login an admin in prod.
 */
export function getDevUser(): {
  id: string;
  email: string;
  isAdmin: boolean;
  permissions: string[];
  useCases: string[];
} | null {
  if (!import.meta.env.DEV || (USER_POOL_ID && CLIENT_ID)) return null;
  return {
    id: 'dev-user',
    email: 'dev@local',
    isAdmin: true,
    permissions: [],
    useCases: ['logistics'],
  };
}

/**
 * Decode JWT token payload (without verification — for UI use only)
 */
function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch {
    return null;
  }
}

/**
 * Extract organization ID from JWT token claims
 * Checks for: custom:organisationId, custom:organizationId, org
 */
export function getOrganizationIdFromToken(token: string): string | null {
  const payload = decodeJwtPayload(token);
  if (!payload) {
    console.warn('[getOrganizationIdFromToken] Failed to decode JWT payload');
    return null;
  }
  
  // Log all claims for debugging (redacted)
  const claimKeys = Object.keys(payload).filter(k => 
    k.includes('org') || k.includes('custom:') || k === 'sub' || k === 'email'
  );
  console.log('[getOrganizationIdFromToken] JWT claims found:', claimKeys);
  console.log('[getOrganizationIdFromToken] Full claim values (org-related):', {
    'custom:organisationId': payload['custom:organisationId'],
    'custom:organizationId': payload['custom:organizationId'],
    'org': payload['org'],
    'organisation': payload['organisation'],
    'organization': payload['organization'],
    'email': payload['email'],
    'cognito:username': payload['cognito:username'],
  });
  
  // Check for custom Cognito attributes (British spelling preferred)
  const orgId = (
    payload['custom:organisationId'] ||
    payload['custom:organizationId'] ||
    payload['org'] ||
    payload['organisation'] ||
    payload['organization'] ||
    null
  );
  
  if (!orgId) {
    console.warn('[getOrganizationIdFromToken] No organization ID found in JWT token - will need to pass as header');
  } else {
    console.log('[getOrganizationIdFromToken] Organization ID found:', orgId);
  }
  
  return orgId;
}

export interface CognitoAuthResult {
  tokens: CognitoTokens;
  challengeName?: string;
  session?: string;
  userAttributes?: Record<string, string>;
}

/**
 * Authenticate user with email and password
 */
export function authenticateUser(email: string, password: string): Promise<CognitoAuthResult> {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: password
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool()
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: (session: CognitoUserSession) => {
        const tokens: CognitoTokens = {
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: session.getRefreshToken().getToken()
        };

        // Log what's in the ID token for debugging
        console.log('[authenticateUser] Login successful, analyzing ID token...');
        const idTokenPayload = decodeJwtPayload(tokens.idToken);
        if (idTokenPayload) {
          console.log('[authenticateUser] ID token claims:', Object.keys(idTokenPayload));
          console.log('[authenticateUser] ID token organisationId:', idTokenPayload['custom:organisationId']);
          console.log('[authenticateUser] ID token all custom claims:', {
            'custom:organisationId': idTokenPayload['custom:organisationId'],
            'custom:organizationId': idTokenPayload['custom:organizationId'],
            'email': idTokenPayload['email'],
            'cognito:username': idTokenPayload['cognito:username'],
            'sub': idTokenPayload['sub'],
          });
        }

        // Store all tokens for fallback access
        localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
        localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
        localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);

        resolve({ tokens });
      },
      onFailure: (err) => {
        reject(err);
      },
      newPasswordRequired: (userAttributes) => {
        // User needs to set a new password
        resolve({
          tokens: null as any,
          challengeName: 'NEW_PASSWORD_REQUIRED',
          session: JSON.stringify({ userAttributes }),
          userAttributes
        });
      }
    });
  });
}

/**
 * Complete new password challenge
 */
export function completeNewPasswordChallenge(
  email: string,
  oldPassword: string,
  newPassword: string
): Promise<CognitoAuthResult> {
  return new Promise((resolve, reject) => {
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: oldPassword
    });

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool()
    });

    cognitoUser.authenticateUser(authenticationDetails, {
      newPasswordRequired: (userAttributes) => {
        // Remove attributes that can't be modified
        delete userAttributes.email_verified;
        delete userAttributes.email;

        cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
          onSuccess: (session: CognitoUserSession) => {
            const tokens: CognitoTokens = {
              accessToken: session.getAccessToken().getJwtToken(),
              idToken: session.getIdToken().getJwtToken(),
              refreshToken: session.getRefreshToken().getToken()
            };

            // Log what's in the ID token for debugging
            console.log('[completeNewPasswordChallenge] Password challenge completed, analyzing ID token...');
            const idTokenPayload = decodeJwtPayload(tokens.idToken);
            if (idTokenPayload) {
              console.log('[completeNewPasswordChallenge] ID token claims:', Object.keys(idTokenPayload));
              console.log('[completeNewPasswordChallenge] ID token organisationId:', idTokenPayload['custom:organisationId']);
            }

            // Store refresh token
            localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
            localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
            localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);

            resolve({ tokens });
          },
          onFailure: (err) => {
            reject(err);
          }
        });
      },
      onSuccess: () => {
        reject(new Error('Expected NEW_PASSWORD_REQUIRED challenge'));
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
}

/**
 * Refresh session using refresh token
 */
export function refreshSession(): Promise<CognitoTokens | null> {
  return new Promise((resolve) => {
    console.log('[refreshSession] 🔄 Starting session refresh...');
    
    // First, try to get current user from Cognito
    const currentUser = getUserPool().getCurrentUser();
    
    if (!currentUser) {
      console.warn('[refreshSession] ⚠️  No current user in Cognito pool, checking localStorage fallback...');
      
      // Fallback: Check if we have stored tokens
      const storedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      const storedIdToken = localStorage.getItem(ID_TOKEN_KEY);
      const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
      
      console.log('[refreshSession] 📦 Stored tokens found:', {
        hasAccessToken: !!storedAccessToken,
        hasIdToken: !!storedIdToken,
        hasRefreshToken: !!storedRefreshToken,
      });
      
      if (storedAccessToken && storedIdToken && storedRefreshToken) {
        // Check if tokens are still valid (basic check - not expired)
        try {
          const accessTokenPayload = JSON.parse(atob(storedAccessToken.split('.')[1]));
          const exp = accessTokenPayload.exp;
          const now = Math.floor(Date.now() / 1000);
          
          console.log('[refreshSession] ⏱️  Token expiration check:', {
            expiresIn: exp - now,
            isExpired: exp < now,
            expiresIn60s: exp < now + 60,
          });
          
          // If token is expired or expires in less than 1 minute, return null
          if (exp && exp < now + 60) {
            console.warn('[refreshSession] ⏰ Stored access token expired or expiring soon, need to refresh');
            // Try to refresh using stored refresh token
            // But we need the username to create a CognitoUser
            const idTokenPayload = JSON.parse(atob(storedIdToken.split('.')[1]));
            const username = idTokenPayload.email || idTokenPayload['cognito:username'];
            
            console.log('[refreshSession] 🔑 Extracted username for refresh:', username);
            
            if (username && storedRefreshToken) {
              const cognitoUser = new CognitoUser({
                Username: username,
                Pool: getUserPool()
              });
              
              cognitoUser.refreshSession(new CognitoRefreshToken({ RefreshToken: storedRefreshToken }), (refreshErr, refreshedSession) => {
                if (refreshErr || !refreshedSession) {
                  console.error('[refreshSession] ❌ Failed to refresh using stored refresh token:', refreshErr);
                  resolve(null);
                  return;
                }
                
                const tokens: CognitoTokens = {
                  accessToken: refreshedSession.getAccessToken().getJwtToken(),
                  idToken: refreshedSession.getIdToken().getJwtToken(),
                  refreshToken: refreshedSession.getRefreshToken().getToken()
                };
                
                console.log('[refreshSession] ✅ Successfully refreshed tokens');
                
                // Update stored tokens
                localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
                localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
                localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
                
                resolve(tokens);
              });
              return;
            }
          } else {
            // Token is still valid, use stored tokens
            console.log('[refreshSession] ✅ Stored tokens are still valid (fallback)');
            resolve({
              accessToken: storedAccessToken,
              idToken: storedIdToken,
              refreshToken: storedRefreshToken
            });
            return;
          }
        } catch (e) {
          console.error('[refreshSession] ❌ Error parsing stored tokens:', e);
        }
      }
      
      console.error('[refreshSession] ❌ No current user and no valid stored tokens');
      resolve(null);
      return;
    }

    console.log('[refreshSession] ✅ Found current user in Cognito pool');
    
    currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err) {
        console.error('[refreshSession] ❌ Session refresh failed:', err);
        // Try to refresh the session using refresh token
        const storedRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
        if (storedRefreshToken) {
          console.log('[refreshSession] 🔄 Attempting to refresh session using stored refresh token...');
          currentUser.refreshSession(new CognitoRefreshToken({ RefreshToken: storedRefreshToken }), (refreshErr, refreshedSession) => {
            if (refreshErr || !refreshedSession) {
              console.error('[refreshSession] Failed to refresh session:', refreshErr);
              resolve(null);
              return;
            }
            
            const tokens: CognitoTokens = {
              accessToken: refreshedSession.getAccessToken().getJwtToken(),
              idToken: refreshedSession.getIdToken().getJwtToken(),
              refreshToken: refreshedSession.getRefreshToken().getToken()
            };
            
            console.log('[refreshSession] ✅ Successfully refreshed session');
            
            // Update stored tokens
            localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
            localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
            localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
            resolve(tokens);
          });
        } else {
          console.error('[refreshSession] ❌ No stored refresh token available');
          resolve(null);
        }
        return;
      }

      if (!session || !session.isValid()) {
        console.warn('[refreshSession] ⚠️  Session is invalid or expired');
        resolve(null);
        return;
      }

      console.log('[refreshSession] ✅ Session is valid');

      const tokens: CognitoTokens = {
        accessToken: session.getAccessToken().getJwtToken(),
        idToken: session.getIdToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken()
      };

      // Update stored tokens
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
      localStorage.setItem(ID_TOKEN_KEY, tokens.idToken);
      
      console.log('[refreshSession] ✅ Returning tokens to caller');
      resolve(tokens);
    });
  });
}

/**
 * Forgot password - initiate password reset
 */
export function forgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool()
    });

    cognitoUser.forgotPassword({
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
}

/**
 * Confirm password reset with verification code
 */
export function confirmPassword(email: string, verificationCode: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: getUserPool()
    });

    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess: () => {
        resolve();
      },
      onFailure: (err) => {
        reject(err);
      }
    });
  });
}

/**
 * Sign out user
 */
export function signOut(): void {
  if (USER_POOL_ID && CLIENT_ID) {
    const currentUser = getUserPool().getCurrentUser();

    if (currentUser) {
      currentUser.signOut();
    }
  }

  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('zecure_current_user');
  localStorage.removeItem('zecure_user_info');
  localStorage.removeItem('zecure_session_token');
}

/**
 * Check if user has a stored session
 */
export function hasStoredSession(): boolean {
  try {
    return !!localStorage.getItem(REFRESH_TOKEN_KEY) || !!getUserPool().getCurrentUser();
  } catch {
    // If Cognito not configured (tests), just check localStorage
    return !!localStorage.getItem(REFRESH_TOKEN_KEY);
  }
}

/**
 * Get current user email from token
 */
export function getCurrentUserEmail(): Promise<string | null> {
  if (!USER_POOL_ID || !CLIENT_ID) {
    return Promise.resolve(null);
  }

  return new Promise((resolve) => {
    const currentUser = getUserPool().getCurrentUser();
    
    if (!currentUser) {
      resolve(null);
      return;
    }

    currentUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) {
        resolve(null);
        return;
      }

      const email = session.getIdToken().payload.email;
      resolve(email || null);
    });
  });
}

/**
 * Sync read of login email from the stored ID token (for stamping `updated_by`).
 * Prefer this for save payloads; falls back to getCurrentUserEmail() when absent.
 */
export function readSessionUserEmail(): string | null {
  try {
    const idToken = localStorage.getItem(ID_TOKEN_KEY);
    if (!idToken) return null;
    const payload = decodeJwtPayload(idToken);
    if (!payload) return null;
    const email = payload.email;
    if (typeof email === 'string' && email.trim()) return email.trim();
    const username = payload['cognito:username'];
    if (typeof username === 'string' && username.includes('@')) return username.trim();
    return null;
  } catch {
    return null;
  }
}

export default {
  authenticateUser,
  completeNewPasswordChallenge,
  refreshSession,
  forgotPassword,
  confirmPassword,
  signOut,
  hasStoredSession,
  getCurrentUserEmail,
  readSessionUserEmail,
  getDevUser,
};
